import { ReactNode } from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { ConditionKind, ConditionPrefix, OfferFormData } from './offerForm';
import OfferSection from './OfferSection';
import OptionToggleGroup from './OptionToggleGroup';

const CONDITION_FIELDS: Record<string, { kind: ConditionKind; title: string; min: string; max: string }> = {
  montant: { kind: 'Montant', title: 'Montant de la transaction', min: "Montant d'achat minimum", max: "Montant d'achat maximum" },
  article: { kind: 'Article', title: "Nombre d'articles", min: 'Quantité minimum', max: 'Quantité maximum' },
  points: { kind: 'Points', title: 'Nombre de points', min: 'Nombre de points minimum', max: 'Nombre de points maximum' },
};

interface ConditionSectionProps<T extends string> {
  title: string;
  prefix: ConditionPrefix;
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T | null;
  onSelect: (value: T | null) => void;
  offer: OfferFormData;
  onChange: (patch: Partial<OfferFormData>) => void;
  // Contrôle affiché avant les options (ex. interrupteur « Immediate use »)
  leading?: ReactNode;
  // Contenu affiché sous les options (ex. sous-conditions), et conditions dont on affiche les champs min/max
  // quand elles diffèrent de `value` (plusieurs possibles)
  belowOptions?: ReactNode;
  fieldKinds?: readonly string[];
}

function ConditionSection<T extends string>({ title, prefix, options, value, onSelect, offer, onChange, leading, belowOptions, fieldKinds }: ConditionSectionProps<T>) {
  const kinds = fieldKinds ?? (value ? [value] : []);
  const fieldsList = kinds.flatMap(kind => (CONDITION_FIELDS[kind] ? [CONDITION_FIELDS[kind]] : []));

  return (
    <OfferSection title={title}>
      {leading}
      <OptionToggleGroup options={options} value={value} onChange={onSelect} />
      {belowOptions}
      {fieldsList.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {fieldsList.map(fields => {
            const minKey = `${prefix}${fields.kind}Min` as const;
            const maxKey = `${prefix}${fields.kind}Max` as const;
            return (
              <Grid key={fields.kind} size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {fields.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField fullWidth size="small" label={fields.min} value={offer[minKey]} onChange={e => onChange({ [minKey]: e.target.value })} />
                  <TextField fullWidth size="small" label={fields.max} value={offer[maxKey]} onChange={e => onChange({ [maxKey]: e.target.value })} />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </OfferSection>
  );
}

export default ConditionSection;
